/**
 * findingsModel.ts — M6 Output Factory · Increment 2 (2026-06-25, Anil).
 *
 * The FINDINGS POOL — a per-project structured store of confirmed evaluation
 * findings. Each finding is the staff-confirmed output of one Evaluation step;
 * the pool is the SINGLE SOURCE the report (Inc 3) draws from. UI contract:
 * `AVBP_EVALUATION_TO_REPORT_PIPELINE_MOCKUP_2026-06-25.html` §1 (the per-tier
 * Evaluation checklist) + memory `project_avbp_output_factory_pipeline`.
 *
 * Owner ruling — the AUO confirm-loop: AI drafts where it can, but NOTHING is
 * pool-ready (`confirmed`) without a staff confirm.
 *
 * This module is PURE (Firebase-free, React-free) so the `.cjs` runner exercises
 * the finding shape + the per-tier checklist + the status transitions. The
 * Firestore CRUD is `findingsStore.ts`; the surface is `EvaluationWorkspace.tsx`.
 *
 * Inc-2 reuse vs stub:
 *   - map_review · grid_selection · zone_auo already have REAL evaluation
 *     outputs (StaffTechnicalPanel + FloorBoundaryTracer + AuoReviewPanel write
 *     boundary/grid/fixtures/auo_decisions). Inc 2 represents them as findings.
 *   - astro_vastu · astro_remedies · health_overlay · verdict are NEW
 *     CONFIRMABLE finding RECORDS — staff fill/confirm; the record can hold or
 *     reference a payload. Deep astro-engine wiring is Inc 3.
 */

/* ════════════════ Finding types (the Evaluation checklist) ════════════════ */

/** The evaluation finding types (mockup §1 checklist, in render order). */
export const FINDING_TYPES = [
  'map_review',     // 🗺 satellite + site — staff marks up & adds input
  'grid_selection', // ▦ stage-keyed grid + which data
  'zone_auo',       // 🎯 16-zone × AUO disposition
  'astro_vastu',    // ✶ per-occupant favourability (chart + readings)
  'astro_remedies', // ☘ dictated / generated remedies
  'health_overlay', // ❤ per-person health considerations
  'verdict',        // ⚖ the overall reading for this project
] as const;
export type FindingType = (typeof FINDING_TYPES)[number];

/** Whether a finding type already has a REAL evaluation output today (Inc 2) or
 * is a new confirmable record whose deep wiring is Inc 3. */
export const FINDING_IS_REAL: Record<FindingType, boolean> = {
  map_review: true,      // StaffTechnicalPanel map/site input
  grid_selection: true,  // FloorBoundaryTracer + grid
  zone_auo: true,        // AuoReviewPanel + auo_decisions confirm-loop
  astro_vastu: false,    // record-only this increment (Inc 3 = engine wiring)
  astro_remedies: false,
  health_overlay: false,
  verdict: false,
};

/** Display metadata for a finding type (icon + label + one-line meta). */
export const FINDING_META: Record<FindingType, { icon: string; label: string; meta: string }> = {
  map_review:     { icon: '🗺', label: 'Map review & input',          meta: 'satellite + site — staff marks up & adds input' },
  grid_selection: { icon: '▦', label: 'Grid selection (stage-keyed)', meta: 'which grid + which data for this stage' },
  zone_auo:       { icon: '🎯', label: 'Zone / AUO analysis',          meta: '16-zone × AUO disposition' },
  astro_vastu:    { icon: '✶', label: 'Astro-Vastu chart & readings',  meta: 'per-occupant favourability' },
  astro_remedies: { icon: '☘', label: 'Astrology-based remedies',      meta: 'dictated / generated remedies' },
  health_overlay: { icon: '❤', label: 'Health overlay',                meta: 'per-person health considerations' },
  verdict:        { icon: '⚖', label: 'Verdict / recommendation',      meta: 'the overall reading for this project' },
};

/* ════════════════ Source & status ════════════════ */

/** How a finding was produced (mockup source-tag). */
export type FindingSource = 'auto' | 'manual' | 'composite';

/**
 * Confirm-loop status. `to_assess` = not started; `ai_drafted` = AI produced a
 * draft awaiting a human; `confirmed` = staff-confirmed → POOL-READY (the only
 * status the report may draw from). Nothing reaches a report un-confirmed.
 */
export type FindingStatus = 'to_assess' | 'ai_drafted' | 'confirmed';

/** Is this finding pool-ready (a report may draw from it)? Only when confirmed. */
export function isPoolReady(status: FindingStatus): boolean {
  return status === 'confirmed';
}

/* ════════════════ The finding record ════════════════ */

/**
 * A finding in the pool. `payload` holds the small confirmed data (a verdict
 * string, a remedy list, a health note); `ref` references heavier evaluation
 * artefacts already persisted elsewhere (e.g. a gridId / floor / storage path)
 * so a finding need not duplicate the boundary/grid blob. Either may be absent
 * for a `to_assess` finding.
 */
export interface Finding {
  /** Doc id (== `findingType` for the canonical one-per-type pool; multi-instance
   * findings — e.g. per-floor grids — append a suffix). */
  id: string;
  projectId: string;
  type: FindingType;
  source: FindingSource;
  status: FindingStatus;
  /** Small confirmed data inline (verdict text, remedy lines, health note…). */
  payload?: Record<string, unknown>;
  /** Reference to a heavier already-persisted artefact (gridId / floor / path). */
  ref?: { kind: string; id: string } | null;
  /** Staff who confirmed (uid). Set when status → confirmed. */
  confirmedBy?: string | null;
  confirmedAt?: string | null;
  /** Audit. */
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string | null;
}

/** PURE — the canonical doc id for the one-per-type pool entry. */
export function findingIdFor(projectId: string, type: FindingType): string {
  return `${projectId}__${type}`;
}

/** PURE — a blank finding for a type (status `to_assess`, source defaulted). */
export function blankFinding(projectId: string, type: FindingType): Finding {
  return {
    id: findingIdFor(projectId, type),
    projectId,
    type,
    source: FINDING_IS_REAL[type] ? 'auto' : 'manual',
    status: 'to_assess',
    payload: {},
    ref: null,
    confirmedBy: null,
    confirmedAt: null,
  };
}

/* ════════════════ Per-tier Evaluation checklist (tier flags) ════════════════ */

/** Same flag vocabulary as the intake per-tier section flags. */
export type ChecklistFlag = 'required' | 'optional' | 'on' | 'off';

export type EvaluationChecklist = Record<FindingType, ChecklistFlag>;

/** Default = the full checklist on (the L3 baseline). */
export const DEFAULT_EVALUATION_CHECKLIST: EvaluationChecklist = {
  map_review: 'required',
  grid_selection: 'required',
  zone_auo: 'required',
  astro_vastu: 'on',
  astro_remedies: 'on',
  health_overlay: 'on',
  verdict: 'required',
};

/**
 * Per-tier overrides (sparse — only what differs from default). L1 = the shortest
 * checklist (frozen/auto report — assessment only, no astro/health depth); L2 =
 * fuller; L3 inherits the full default. Mirrors the intake TIER_SECTION_FLAGS
 * pattern. Keyed by the SERVICE key (`_R{n}` stripped).
 */
export const TIER_EVALUATION_CHECKLIST: Record<string, Partial<EvaluationChecklist>> = {
  // L1 — frozen/auto report, shortest checklist: map + grid + zone/AUO.
  // 2.3 — `verdict` is OFF for L1A: the project verdict has a SINGLE store,
  // `answers.verdict` (Step 5 / saveVerdict → the deployed report reads it), so a
  // separate (unwired) `verdict` finding would only diverge. The pool-ready gate
  // for L1A therefore covers the REAL evaluation artefacts: map · grid · zone/AUO.
  L1A: { astro_vastu: 'off', astro_remedies: 'off', health_overlay: 'off', verdict: 'off' },
  L1B: { astro_vastu: 'optional', astro_remedies: 'off', health_overlay: 'optional' },
  L1C: { astro_vastu: 'optional', astro_remedies: 'off', health_overlay: 'optional' },
  // L2 — fuller: astro on, health optional.
  L2A: { astro_vastu: 'on', astro_remedies: 'optional', health_overlay: 'optional' },
  L2B: { astro_vastu: 'required', astro_remedies: 'on', health_overlay: 'on' },
  L2C: { astro_vastu: 'on', astro_remedies: 'on', health_overlay: 'optional' },
  L2D: { astro_vastu: 'optional', astro_remedies: 'optional', health_overlay: 'off' },
  // L3 — the full default (everything required/on) — no override needed; listed
  // explicitly so it shows as a configured tier.
  L3A: {},
  L3B: {},
};

/** PURE — the evaluation checklist for a tier (default merged with overrides). */
export function evaluationChecklistForTier(tier: string): EvaluationChecklist {
  const key = (tier || '').replace(/_R\d+$/i, '').toUpperCase();
  const over = TIER_EVALUATION_CHECKLIST[key] || {};
  return { ...DEFAULT_EVALUATION_CHECKLIST, ...over };
}

/** PURE — is a finding type SHOWN in the checklist for this tier? */
export function checklistShown(flag: ChecklistFlag): boolean {
  return flag !== 'off';
}
/** PURE — is a finding type REQUIRED for this tier? */
export function checklistRequired(flag: ChecklistFlag): boolean {
  return flag === 'required';
}

/** PURE — the finding types this tier shows, in canonical order. */
export function checklistTypesForTier(tier: string): FindingType[] {
  const cl = evaluationChecklistForTier(tier);
  return FINDING_TYPES.filter((t) => checklistShown(cl[t]));
}

/* ════════════════ Pool readiness (drives the report gate later) ════════════════ */

/**
 * PURE — the evaluation completeness for a tier: which REQUIRED findings are
 * confirmed vs outstanding. Inc 3's report-generate will gate on this; Inc 2 just
 * surfaces it in the workspace.
 */
export function evaluationProgress(
  tier: string,
  findings: Finding[],
): {
  requiredTypes: FindingType[];
  confirmedRequired: FindingType[];
  outstandingRequired: FindingType[];
  poolReady: Finding[];
  complete: boolean;
} {
  const cl = evaluationChecklistForTier(tier);
  const byType = new Map<FindingType, Finding>();
  for (const f of findings) byType.set(f.type, f);
  const requiredTypes = FINDING_TYPES.filter((t) => checklistRequired(cl[t]));
  const confirmedRequired = requiredTypes.filter((t) => {
    const f = byType.get(t);
    return !!f && isPoolReady(f.status);
  });
  const outstandingRequired = requiredTypes.filter((t) => !confirmedRequired.includes(t));
  const poolReady = findings.filter((f) => isPoolReady(f.status));
  return {
    requiredTypes,
    confirmedRequired,
    outstandingRequired,
    poolReady,
    complete: outstandingRequired.length === 0,
  };
}
